// src/configs/apps/appsindico/appsindico.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  AppstoreOutlined,
  CarOutlined,
  DashboardOutlined,
  FormOutlined,
  FunnelPlotOutlined,
  GlobalOutlined,
  HomeOutlined,
  MessageOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  TagsOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { BsBoxSeam, BsBuilding } from 'react-icons/bs';
import { GrGroup } from 'react-icons/gr';
import { PiToolbox } from 'react-icons/pi';
import { RiShieldUserLine } from 'react-icons/ri';
import { LuDraftingCompass } from 'react-icons/lu';
import { PiUserFocus } from 'react-icons/pi';
import { MdApartment, MdLocalParking } from 'react-icons/md';
import { HiOutlineViewGridAdd } from 'react-icons/hi';

// *******************************************************************************
// APP GLOBAL CONFIGURATION
// *******************************************************************************
const APP_SINDICO = {
  ID: 'appsindico',
  NAME: 'Sindico ERP',
  THEME_COLOR: 'CUSTOM_SINDICO_APP',
  THEME: 'light',
  FINTECH_SERVICES: false,
  DOMAINS: [
    'app.sindicoerp.com',
    'homologation.sindicoerp.com',
    'sistema.sindicoerp.com',
    'homologacao.sindicoerp.com',

    'app.sindicoerp.com.br',
    'homologation.sindicoerp.com.br',
    'sistema.sindicoerp.com.br',
    'homologacao.sindicoerp.com.br',

    'appsindico-v2.web.app',
    'appsindico-v2-homologation.web.app',
  ],
  PRESET: {
    MULTI_SUBSCRIPTIONS: false,
    PROFILE: null,
    NATIVE_RESOURCES: ['moduleresources_omnichannel'],
    SUGGESTED_RESOURCES: [],
    CAN_CHANGE_RESOURCES: true,
    SHOW_RESOURCES_FIRST_CONFIGURATION: true,
    SHOW_RESOURCES_SUBSCRIPTION: true,
    CONTRACT: null,
  },
  COPY_RIGHT: 'Sindico ERP',
  API_PORT_HOMOLOGATION: 8142,
  API_PORT_PRODUCTION: 8042,
  API_HOST_LOCALHOST: 'localhost',
  API_HOST_PRODUCTION: 'vps.jarvisly.com',
  API_INTEGRATED: false,
  TOKEN: import.meta.env.VITE_APPSINDICO_TOKEN,
  TOKEN_EXP_MINUTES: 60,

  LOCALE: 'pt-BR',
  LANGUAGE: 'pt-BR',
  LANGUAGE_OPTIONS: ['pt-BR', 'pt-PT', 'en-US', 'es-ES'],

  LOGO_APP: '/apps/appsindico/logo-app.png',
  LOGO_APP_WHITE: '/apps/appsindico/logo-app-white.png',

  LOGO_APP_ICON: '/apps/appsindico/logo-app-icon.png',
  LOGO_APP_ICON_WHITE: '/apps/appsindico/logo-app-icon-white.png',

  LOGO: '/apps/appsindico/logo.png',
  LOGO_WHITE: '/apps/appsindico/logo-white.png',
  LOGO_LOGIN_SIDE: '/apps/appsindico/logo-app-side.png',

  PWA_NAME: { production: 'Sindico ERP', homologation: 'Sindico ERP Test' },
  PWA_SHORT_NAME: { production: 'Sindico', homologation: 'Sindico Test' },
  PWA_DESCRIPTION: 'Gestao de condominios e administradoras',
  TITLE: 'Sindico ERP',
  SLOGAN: '',
  SLOGAN2: '',
  SLOGAN_REGISTRATION: 'registration_slogan',
  SLOGAN_REGISTRATION2: '',
  APPLICATION_LAYER: 'adm',
  LOGIN_IDENTIFICATION: 'email',

  SELF_REGISTRATION: {
    homologation: false,
    production: false,
  },
  SELF_REGISTRATION_MOBILE_VALIDATION: ['pt-BR'],
  USER_INVITATION_MOBILE_VALIDATION: ['pt-BR'],

  GOOGLE_LOGIN: false,
  FACEBOOK_LOGIN: false,
  RESET_PASSWORD: true,
  NAVIGATION_TYPE: 'side',
  MODULES: ['customers', 'companies', 'dataSearch', 'crm', 'landingPages', 'offers', 'leads', 'units', 'unitProfiles', 'parkingSpots', 'omnichannel'],
  SHOW_ALL_MENUS: false,
  BREADCRUMB_COLOR: '#FAFAFBFF',

  WELCOME_NEXT_STEPS: [
    'explore_the_system',
    'complete_your_profile',
    'create_your_first_subscription',
  ],

  MENU_APPLICATION: [
    {
      path: '/app/account',
      title: 'account_settings',
      icon: SettingOutlined,
      moduleId: 'account',
    },
  ],

  MENU_NAVIGATION: [
    // ******************
    // DASHBOARD
    // ******************
    {
      path: '/app/dashboard/overview',
      title: 'home',
      icon: HomeOutlined,
      moduleId: 'welcome',
      isGroupTitle: true,
    },

    // ******************
    // QUERIES (CONSULTAS)
    // ******************
    {
      path: '/app/queries',
      title: 'queries',
      icon: SearchOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'people_and_companies',
          icon: SearchOutlined,
          moduleId: 'dataSearch',
          requiredResources: ['moduleresources_lookup'],
        },
      ],
    },

    // ******************
    // REGISTRATIONS (CADASTROS)
    // ******************
    {
      path: '/app/registrations',
      title: 'records',
      icon: FormOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'products',
          icon: BsBoxSeam,
          moduleId: 'products',
          requiredResources: ['moduleresources_products'],
        },
        {
          title: 'services',
          icon: ToolOutlined,
          moduleId: 'services',
          requiredResources: ['moduleresources_services', 'moduleresources_service_orders'],
        },
        {
          type: 'divider',
        },
        {
          title: 'companies',
          icon: BsBuilding,
          moduleId: 'companies',
          requiredResources: ['moduleresources_multicompany'],
        },
        {
          title: 'customers',
          icon: GrGroup,
          moduleId: 'customers',
          requiredResources: ['moduleresources_crm'],
        },
        {
          title: 'users',
          icon: RiShieldUserLine,
          moduleId: 'users',
          requiredResources: ['moduleresources_multiuser'],
        },
      ],
    },

    // ******************
    // MANAGEMENT (GESTAO DO CONDOMINIO)
    // ******************
    {
      path: '/app/management',
      title: 'management',
      icon: AppstoreOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'units',
          icon: MdApartment,
          moduleId: 'units',
          requiredResources: [],
        },
        {
          title: 'unit_profiles',
          icon: HiOutlineViewGridAdd,
          moduleId: 'unitProfiles',
          requiredResources: [],
        },
        {
          title: 'parking_spots',
          icon: MdLocalParking,
          moduleId: 'parkingSpots',
          requiredResources: [],
        },
      ],
    },

    // ******************
    // CRM
    // ******************
    {
      path: '/app/commercial',
      title: 'commercial',
      icon: RocketOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'omnichannel',
          icon: MessageOutlined,
          moduleId: 'omnichannel',
          requiredResources: ['moduleresources_omnichannel'],
        },

        {
          title: 'landing_pages',
          icon: LuDraftingCompass,
          moduleId: 'landingPages',
          requiredResources: ['moduleresources_landingpages'],
        },

        {
          title: 'leads',
          icon: PiUserFocus,
          moduleId: 'leads',
          requiredResources: ['moduleresources_leads'],
        },

        {
          title: 'crm',
          icon: FunnelPlotOutlined,
          moduleId: 'crm',
          requiredResources: ['moduleresources_crm'],
        },
      ],
    },
  ],

  FIREBASE_CONFIG: JSON.parse(import.meta.env.VITE_APPSINDICO_FIREBASE_CONFIG),
  MOCKUP: {
    AUTH: {
      SIGNIN: {
        email: 'me@biruel.dev',
        password: 'idadm123',
      },
      SIGNUP: {
        countryCode: 'BR',
        name: 'Suporte Sindico',
        email: 'me@biruel.dev',
        emailCode: 1234,
        emailConfirmation: true,
        mobileCode: 1234,
        mobileConfirmation: true,
        password: 'idadm123',
      },
      RESET_PASSWORD: {
        email: 'me@biruel.dev',
        emailCode: 1234,
        password: 'idadm123',
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default APP_SINDICO;
